/**
 * BiometricCaptureView.tsx
 *
 * Shared camera viewport for live biometric capture — used by both
 * FaceScanIdentificationModal (SmartClock / SuperSmartClock) and the public
 * /biometric/[token] link page so the capture UX stays identical everywhere.
 *
 * Scope: the video preview + detection overlays only. Headers, employee
 * cards, capture buttons, tips and page chrome stay with the consumer.
 */

'use client';

import React, { useState } from 'react';
import { EMBEDDED_FAVICON } from '@/lib/embeddedAssets';
import { Activity, Eye, Gauge, Move, Sun } from 'lucide-react';
import FaceMeshOverlay from './FaceMeshOverlay';
import type { FaceDetectionResult } from '@/hooks/useFacialRecognition';
import type { ExtendedBiometricCaptureState } from '../hooks/useStableBiometricCaptureState';
import {
  ATTENDANCE_BIOMETRIC_FACE_GUIDE_DIMENSIONS,
  ATTENDANCE_BIOMETRIC_PREVIEW_ASPECT_CLASS,
} from '../constants/biometricCameraFraming';

export interface BiometricCaptureQualityThresholds {
  presence?: number;
  stability?: number;
  quality?: number;
}

interface BiometricCaptureViewProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  overlayCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** Mirror the preview for the front-facing camera. */
  mirrored?: boolean;
  /** Latest raw detection result (drives the quality meters). */
  detection: FaceDetectionResult | null;
  detected: boolean;
  ready: boolean;
  captureState: ExtendedBiometricCaptureState;
  /** Hold-still countdown pulse (from useHoldStillGate) — forwarded to the
   *  guide overlay so the marker flashes once per pulse. */
  holdFlashPulse?: number;
  /** Bottom status pill text (stabilized). Omit to hide the pill. */
  statusMessage?: string;
  /** Optional content rendered inside the status pill below the message (e.g. retry counter, quality dots). */
  statusFooter?: React.ReactNode;
  /** Show the pill only on small screens (consumers with a side panel). */
  statusPillMobileOnly?: boolean;
  /** Compact quality meters overlay. */
  showMeters?: boolean;
  /** Restrict meters to small screens (consumer has a desktop metrics panel). */
  metersMobileOnly?: boolean;
  /** Branded blocking overlay while camera/detector initializes. */
  initializing?: boolean;
  initMessage?: string;
  /** Per-metric pass thresholds for meter colouring (dynamic per context). */
  qualityThresholds?: BiometricCaptureQualityThresholds;
  /** Extra classes for the outer viewport container (width constraints etc). */
  className?: string;
}

function meterColorClass(value: number, threshold: number): string {
  if (value >= threshold) return 'bg-green-500';
  if (value >= threshold * 0.7) return 'bg-yellow-500';
  return 'bg-red-500';
}

export function statusPillClass(state: ExtendedBiometricCaptureState): string {
  switch (state) {
    case 'ready':
      return 'bg-emerald-500/90 border border-emerald-300/50';
    case 'detected':
      return 'bg-sky-500/90 border border-sky-300/50';
    case 'resetting':
      return 'bg-amber-500/90 border border-amber-300/50';
    case 'move_closer':
    case 'move_back':
    case 'hold_still':
    case 'improve_lighting':
      return 'bg-orange-500/90 border border-orange-300/50';
    case 'searching':
    default:
      return 'bg-slate-800/90 border border-slate-600/40';
  }
}

interface MeterSpec {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  value: number;
  threshold: number;
}

export default function BiometricCaptureView({
  videoRef,
  canvasRef,
  overlayCanvasRef,
  mirrored = true,
  detection,
  detected,
  ready,
  captureState,
  holdFlashPulse,
  statusMessage,
  statusFooter,
  statusPillMobileOnly = false,
  showMeters = true,
  metersMobileOnly = false,
  initializing = false,
  initMessage = 'Initializing face detection...',
  qualityThresholds,
  className = '',
}: BiometricCaptureViewProps) {
  // Hide the <video> until it is actually playing — Android WebView draws a
  // native "tap to play" glyph over an idle video, and our scaleX(-1) mirror
  // renders it inverted. opacity-0 keeps the glyph out of view entirely.
  const [videoPlaying, setVideoPlaying] = useState(false);

  const metrics = detection?.detected ? detection.metrics : null;

  const meters: MeterSpec[] = metrics
    ? [
        {
          key: 'presence',
          label: 'Presence',
          icon: Eye,
          iconClass: 'text-blue-400',
          value: metrics.presence,
          threshold: qualityThresholds?.presence ?? 0.5,
        },
        {
          key: 'quality',
          label: 'Quality',
          icon: Gauge,
          iconClass: 'text-green-400',
          value: metrics.quality,
          threshold: qualityThresholds?.quality ?? 0.35,
        },
        {
          key: 'stability',
          label: 'Stability',
          icon: Activity,
          iconClass: 'text-purple-400',
          value: metrics.stability,
          threshold: qualityThresholds?.stability ?? 0.5,
        },
        ...(detection?.lighting
          ? [{
              key: 'lighting',
              label: 'Lighting',
              icon: Sun,
              iconClass: 'text-yellow-400',
              value: detection.lighting.lightingScore,
              threshold: 0.5,
            }]
          : []),
        ...(detection?.distance
          ? [{
              key: 'distance',
              label: 'Distance',
              icon: Move,
              iconClass: 'text-pink-400',
              value: detection.distance.distanceScore,
              threshold: 0.5,
            }]
          : []),
      ]
    : [];

  return (
    <div className={`relative bg-gray-900 rounded-xl overflow-hidden ${ATTENDANCE_BIOMETRIC_PREVIEW_ASPECT_CLASS} ${className}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        onPlaying={() => setVideoPlaying(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${videoPlaying ? 'opacity-100' : 'opacity-0'}`}
        style={{ transform: mirrored ? 'scaleX(-1)' : 'none' }}
      />
      <canvas ref={canvasRef} className="hidden" />
      {/* Detection box canvas — z-[6] keeps it above the FaceMeshOverlay
          scrim (z-5) so the tracked-face indicator isn't dimmed/revealed
          only through the oval cutout. */}
      <canvas
        ref={overlayCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-[6]"
        style={{ transform: mirrored ? 'scaleX(-1)' : 'none' }}
      />

      <FaceMeshOverlay
        detected={detected}
        ready={ready}
        state={captureState}
        flashPulse={holdFlashPulse}
        width={ATTENDANCE_BIOMETRIC_FACE_GUIDE_DIMENSIONS.width}
        height={ATTENDANCE_BIOMETRIC_FACE_GUIDE_DIMENSIONS.height}
      />

      {/* Compact quality meters overlay */}
      {showMeters && meters.length > 0 && (
        <div className={`absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-xl p-2 space-y-1.5 min-w-[110px] z-20 ${metersMobileOnly ? 'md:hidden' : ''}`}>
          {meters.map(({ key, label, icon: Icon, iconClass, value, threshold }) => (
            <div key={key} className="flex items-center gap-1.5" title={label}>
              <Icon className={`w-3 h-3 ${iconClass} flex-shrink-0`} />
              <div className="flex-1 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${meterColorClass(value, threshold)}`}
                  style={{ width: `${Math.min(value * 100, 100)}%` }}
                />
              </div>
              <span className="text-[10px] text-white/80 w-6 text-right">{Math.round(value * 100)}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom status pill — z-10 keeps it above the FaceMeshOverlay scrim */}
      {statusMessage && !initializing && (
        <div className={`absolute z-10 bottom-3 left-3 right-3 ${statusPillMobileOnly ? 'md:hidden' : ''}`}>
          <div className={`rounded-xl p-2.5 text-center backdrop-blur-sm transition-all duration-500 ease-out ${statusPillClass(captureState)}`}>
            <p className="text-[13px] font-medium text-white sm:text-sm">{statusMessage}</p>
            {statusFooter}
          </div>
        </div>
      )}

      {/* Branded init overlay */}
      {initializing && (
        <div className="absolute inset-0 bg-black flex flex-col items-center justify-center z-10" style={{ paddingBottom: '20%' }}>
          <div className="relative mb-4">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={EMBEDDED_FAVICON} alt="Loading" className="w-16 h-16" />
            <div className="absolute inset-0 -m-1.5 w-[76px] h-[76px] border-[3px] border-transparent border-t-blue-500 border-r-blue-500 rounded-full animate-spin" />
          </div>
          <p className="text-white text-sm md:text-base font-semibold">{initMessage}</p>
          <p className="text-white/60 text-xs mt-2">Please wait...</p>
        </div>
      )}
    </div>
  );
}
